import React, { useState } from 'react';
import {
  Box,
  TextField,
  Typography,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Button,
  Divider,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIndicatorIcon
} from '@mui/icons-material';
import { Package, PackageType } from '../../../types/Venue';

interface PackageIncludedItemsFormProps {
  packageData: Partial<Package>;
  onChange: (data: Partial<Package>) => void;
}

const PackageIncludedItemsForm: React.FC<PackageIncludedItemsFormProps> = ({
  packageData,
  onChange
}) => {
  const [newItem, setNewItem] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleAddItem = () => {
    if (!newItem.trim()) {
      setError('Item cannot be empty');
      return;
    }

    const updatedItems = [...(packageData.includesItems || []), newItem.trim()];
    onChange({ includesItems: updatedItems });
    setNewItem('');
    setError(null);
  };

  const handleRemoveItem = (index: number) => {
    const updatedItems = [...(packageData.includesItems || [])];
    updatedItems.splice(index, 1);
    onChange({ includesItems: updatedItems });
  };

  const handleItemChange = (index: number, newValue: string) => {
    const updatedItems = [...(packageData.includesItems || [])];
    updatedItems[index] = newValue;
    onChange({ includesItems: updatedItems });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddItem();
    }
  };

  const moveItem = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= (packageData.includesItems || []).length) return;
    
    const updatedItems = [...(packageData.includesItems || [])];
    const [movedItem] = updatedItems.splice(fromIndex, 1);
    updatedItems.splice(toIndex, 0, movedItem);
    
    onChange({ includesItems: updatedItems });
  };

  return (
    <Box sx={{ py: 2 }}>
      <Typography variant="h6" gutterBottom>
        What's Included in this Package?
      </Typography>
      
      <Typography variant="body2" color="text.secondary" paragraph>
        Add all items, services, or experiences that are included in this package.
        Be specific and highlight the value customers will receive.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', mb: 2 }}>
            <TextField
              fullWidth
              id="newItem"
              label="New Item"
              value={newItem}
              onChange={(e) => setNewItem(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="e.g., 3-course dinner for two"
              sx={{ mr: 1 }}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddItem}
            >
              Add
            </Button>
          </Box>
        </Grid>
        
        <Grid item xs={12}>
          <Paper sx={{ maxHeight: 300, overflow: 'auto' }}>
            {(packageData.includesItems || []).length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No items added yet. Add items that are included in your package.
                </Typography>
              </Box>
            ) : (
              <List>
                {(packageData.includesItems || []).map((item, index) => (
                  <React.Fragment key={index}>
                    {index > 0 && <Divider component="li" />}
                    <ListItem>
                      <IconButton
                        edge="start"
                        size="small"
                        sx={{ mr: 1, cursor: 'grab' }}
                      >
                        <DragIndicatorIcon />
                      </IconButton>
                      
                      <ListItemText
                        primary={
                          <TextField
                            fullWidth
                            value={item}
                            onChange={(e) => handleItemChange(index, e.target.value)}
                            variant="standard"
                            InputProps={{
                              disableUnderline: true
                            }}
                          />
                        }
                      />
                      
                      <ListItemSecondaryAction>
                        <IconButton 
                          edge="end" 
                          onClick={() => handleRemoveItem(index)}
                          size="small"
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                        
                        <Box component="span" sx={{ ml: 1 }}>
                          <IconButton 
                            size="small"
                            disabled={index === 0}
                            onClick={() => moveItem(index, index - 1)}
                          >
                            ↑
                          </IconButton>
                          <IconButton 
                            size="small"
                            disabled={index === (packageData.includesItems || []).length - 1}
                            onClick={() => moveItem(index, index + 1)}
                          >
                            ↓
                          </IconButton>
                        </Box>
                      </ListItemSecondaryAction>
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>
      
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Suggested Items for {packageData.packageType || 'Dinner'} Package:
        </Typography>
        <Grid container spacing={1}>
          {getSuggestedItems(packageData.packageType).map((suggestion, index) => (
            <Grid item key={index}>
              <Button 
                variant="outlined" 
                size="small"
                onClick={() => {
                  if (!(packageData.includesItems || []).includes(suggestion)) {
                    onChange({ 
                      includesItems: [...(packageData.includesItems || []), suggestion] 
                    });
                  }
                }}
                sx={{ mb: 1 }}
              >
                {suggestion}
              </Button>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

const getSuggestedItems = (packageType?: PackageType): string[] => {
  switch(packageType) {
    case 'dinner':
      return [
        'Three-course dinner for two',
        'Bottle of house wine',
        'Reserved private table',
        'Complimentary dessert',
        'Specialty cocktails',
        'Chef\'s welcome appetizer',
        'Valet parking'
      ];
    case 'experience':
      return [
        'Guided tour',
        'Welcome drinks',
        'Souvenir photo',
        'Private experience guide',
        'Transportation',
        'Equipment rental',
        'Light refreshments'
      ];
    case 'event':
      return [
        'Event space for 2-4 hours',
        'Basic setup and cleanup',
        'Audio/visual equipment',
        'Dedicated event staff',
        'Catering options',
        'Decorations package',
        'Photography service'
      ];
    case 'special':
      return [
        'Champagne toast',
        'Flower arrangement',
        'Private dining room',
        'Customized menu',
        'Live music',
        'VIP service',
        'Special occasion cake'
      ];
    default:
      return [
        'Welcome drink',
        'Appetizer',
        'Main course',
        'Dessert',
        'Beverage',
        'Service charge',
        'Tax included'
      ];
  }
};

export default PackageIncludedItemsForm;
